import React, { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import {
  APPROPRIATENESS_TEST_MAX_ATTEMPTS,
  canTakeTest,
  getAppropriatenessTestRemainingRetakeDays,
  getDueDateMessage,
  getRemainingWaitingTime,
} from '@pages/ProductRegistration/AppropriatenessTest/appropriatenessTestStatus'
import { useProductAppropriatenessTestStatus } from '@pages/ProductRegistration/AppropriatenessTest/hooks/useProductAppropriatenessTestStatus'
import { useResolvedAppTestProduct } from '@pages/ProductRegistration/AppropriatenessTest/hooks/useResolvedAppTestProduct'
import { useProductRegistrationExit } from '@pages/ProductRegistration/hooks/useProductRegistrationExit'

import { useAccountReadContext, useAccountWriteContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Button, IconButton } from '@ui/components/Button'
import { ContentBlock } from '@ui/components/ContentBlock/ContentBlock'
import { FeatureIcon } from '@ui/components/FeatureIcon/FeatureIcon'
import { Link } from '@ui/components/Link'
import { CircleCloseIcon } from '@ui/icons/CircleClose.icon'
import { CloseIcon } from '@ui/icons/Close.icon'
import { InfoIcon } from '@ui/icons/Info.icon'

import { hasConsentedToLegalDocuments } from '@utils/AccountAccess/accountDocumentStatuses'
import {
  hasProductValidationDeniedStatus,
  isAppropriatenessTestMaximumRetakeLimitReached,
  isBaseProduct,
  isEconomicProfileTestNeeded,
  isProductAppropriatenessInvalid,
  showApptestRiskScreen,
} from '@utils/AccountAccess/accountProductStatuses'
import { getProductAppTestRiskAcceptancePath, getProductEconomicProfilePath } from '@utils/path'

import { isAccountActivatedStatus } from '@model/AccountDetailedStatus'
import { TestAnswerOutcomeStatus } from '@model/ClientTestAnswerDto'
import {
  TickmillProductType,
  isTickmillProductTypeCFD,
  isTickmillProductTypeETD,
} from '@model/TickmillProductType'

import { useHomePageUrl } from '@hooks/useHomePageUrl'
import { useLandingPage } from '@hooks/useLandingPage'
import { useTryProduct } from '@hooks/useTryProduct'

import { Box } from '@shared/components/Box'
import { ActionModal } from '@shared/modals/Action.modal'
import { FullScreenFooter, FullScreenLayout } from '@shared/templates/FullScreenLayout'

export const ProductAppropriatenessTestRequiredPage: FC = () => {
  const { t } = useTranslation()
  const { account } = useAccountReadContext()
  const { refreshAccount } = useAccountWriteContext()
  const locale = useSessionLanguage()
  const navigate = useNavigate()
  const resolvedProductId = useResolvedAppTestProduct()
  const homePageUrl = useHomePageUrl()
  const { getLandingPage } = useLandingPage()
  const { apTestAnswers, attempt, isLoading } =
    useProductAppropriatenessTestStatus(resolvedProductId)
  const handleExit = useProductRegistrationExit(resolvedProductId)
  const exitConfirmationModal = useModal(ActionModal, { onConfirm: handleExit })
  const appTestPath = `/dashboard/product-registration/${resolvedProductId}/appropriateness-test`
  const legalDocumentsPath = `/dashboard/product-registration/${resolvedProductId}/legal-documents`
  const appTestRiskPath = getProductAppTestRiskAcceptancePath(resolvedProductId)
  const economicProfilePath = getProductEconomicProfilePath(resolvedProductId)
  const testStartPath = hasConsentedToLegalDocuments(account, resolvedProductId)
    ? appTestPath
    : legalDocumentsPath

  useEffect(() => {
    refreshAccount(locale)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale])

  const isSQFProduct = resolvedProductId === TickmillProductType.SQF
  const isMarginalPass =
    apTestAnswers?.outcome?.outcome === TestAnswerOutcomeStatus.REFERRED ||
    apTestAnswers?.outcome?.outcome === TestAnswerOutcomeStatus.MARGINAL
  const hasDeclinedRisk =
    isSQFProduct && isMarginalPass && hasProductValidationDeniedStatus(account, resolvedProductId)

  useEffect(() => {
    if (isLoading) return

    if (apTestAnswers?.isClientVulnerable) {
      navigate(`${appTestPath}/denied`, { replace: true })
      return
    } else if (hasDeclinedRisk) {
      navigate(`${appTestPath}/risk-declined`, { replace: true })
      return
    }

    if (
      apTestAnswers?.outcome?.outcome === TestAnswerOutcomeStatus.FAILED &&
      !isBaseProduct(resolvedProductId)
    ) {
      navigate(`${appTestPath}/failed`, { replace: true })
      return
    }

    const requiresEconomicProfile = isEconomicProfileTestNeeded(account, resolvedProductId)
    const requiresAppropriatenessTest = isProductAppropriatenessInvalid(resolvedProductId, account)

    if (showApptestRiskScreen(account, resolvedProductId)) {
      navigate(requiresEconomicProfile ? appTestRiskPath : `${appTestPath}/risk`, { replace: true })
      return
    }

    if (requiresEconomicProfile && !requiresAppropriatenessTest) {
      navigate(economicProfilePath, { replace: true })
      return
    }

    if (!requiresEconomicProfile && !requiresAppropriatenessTest) {
      navigate(getLandingPage(resolvedProductId), { replace: true })
    }
  }, [
    account,
    apTestAnswers?.isClientVulnerable,
    apTestAnswers?.outcome?.outcome,
    appTestPath,
    appTestRiskPath,
    economicProfilePath,
    getLandingPage,
    hasDeclinedRisk,
    isLoading,
    navigate,
    resolvedProductId,
  ])

  const cfdInfo = useTryProduct(TickmillProductType.CFD)
  const etdInfo = useTryProduct(TickmillProductType.ETD)

  const mergedBaseTest =
    isBaseProduct(resolvedProductId) &&
    cfdInfo.isRegistered &&
    etdInfo.isRegistered &&
    !cfdInfo.isActivated &&
    !etdInfo.isActivated

  const isCFD = isTickmillProductTypeCFD(resolvedProductId) || mergedBaseTest
  const isETD = isTickmillProductTypeETD(resolvedProductId) || mergedBaseTest

  const isMaxAttemptsReached =
    isAppropriatenessTestMaximumRetakeLimitReached(account, resolvedProductId) ||
    (attempt !== undefined && attempt >= APPROPRIATENESS_TEST_MAX_ATTEMPTS)
  const remainingWaitingTime = getRemainingWaitingTime(account, apTestAnswers)
  const isTakeTestAllowed = canTakeTest(account, apTestAnswers, resolvedProductId)
  const isFailedOutcome = apTestAnswers?.outcome?.outcome === TestAnswerOutcomeStatus.FAILED
  const remainingDays = getAppropriatenessTestRemainingRetakeDays(apTestAnswers)
  const title = isMaxAttemptsReached
    ? t('Profile.Trading Experience Test Failed!')
    : t('Required Step: Complete the Appropriateness Test')
  const description = isMaxAttemptsReached
    ? [
        t(
          'Profile.Maximum attempts (3/3) reached\\. You will be able to take the appropriateness test in [365] days',
          { remainingDays }
        ),
      ]
    : isFailedOutcome
    ? [
        t(
          'Profile.Client Area and opening a Trading Account will only be available upon satisfactory completion of appropriateness test.'
        ),
      ]
    : [
        t('Sign up.Answer all questions with accurate information'),
        t(
          'To gain access to your Client Area and open a Trading Account, you must first pass the Appropriateness Test.'
        ),
        t('Sign up.We will evaluate your trading knowledge, trading experience'),
      ]

  if (isFailedOutcome && !isMaxAttemptsReached) {
    description.push(getDueDateMessage(t, attempt, remainingWaitingTime))
  }

  const hasTopSlot = isAccountActivatedStatus(account)

  return (
    <FullScreenLayout
      isLoading={isLoading || !isBaseProduct(resolvedProductId)}
      headerLeftSlot={
        hasTopSlot && (
          <IconButton.Ghost icon={CloseIcon} onClick={() => exitConfirmationModal.open()} />
        )
      }
    >
      <Box flex direction='column' gap='x32' align='center' minHeight='100%'>
        <ContentBlock
          variant='voucher'
          icon={
            isMaxAttemptsReached ? (
              <FeatureIcon
                size='lg'
                icon={CircleCloseIcon}
                bg='surface-danger-high-em'
                color='text-white'
              />
            ) : (
              <FeatureIcon size='lg' icon={InfoIcon} bg='surface-info-p-high-em' />
            )
          }
          title={title}
          description={description}
        />
        <FullScreenFooter>
          <Button.Primary
            size='lg'
            fullWidth
            disabled={!isTakeTestAllowed || isMaxAttemptsReached}
            onClick={() => navigate(testStartPath)}
          >
            {t('Proceed to the Test')}
          </Button.Primary>
          {isCFD && (
            <Link href={`${homePageUrl}/trading/demo-account`}>
              {t('Profile.Get Your Free CFD Demo Account')}
            </Link>
          )}
          {isETD && (
            <Link href={`${homePageUrl}/futures#`}>
              {t('Profile.Get Your Free Futures Demo Account')}
            </Link>
          )}
        </FullScreenFooter>
      </Box>
    </FullScreenLayout>
  )
}
